import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AppShell } from "../components/AppShell";
import { PageHeader } from "../components/PageHeader";
import { Toolbar, ToolbarGroup } from "../components/Toolbar";
import { currentUtcMonth, fetchCalendarMonth, shiftMonth } from "../lib/readingsApi";
import type { CalendarDayDto, CalendarMonthResponse } from "../lib/types";

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function monthLabel(month: string): string {
  const [y, m] = month.split("-").map(Number);
  return new Date(Date.UTC(y!, m! - 1, 1)).toLocaleString("en-US", {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  });
}

function statusChip(packs: CalendarDayDto["packs"]): string {
  if (packs.length === 0) return "empty";
  if (packs.some((p) => p.status === "published")) return "published";
  if (packs.some((p) => p.status === "reviewed")) return "reviewed";
  return "draft";
}

export function ReadingsCalendarPage() {
  const [month, setMonth] = useState(currentUtcMonth);
  const [data, setData] = useState<CalendarMonthResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const result = await fetchCalendarMonth(month);
        if (!cancelled) setData(result);
      } catch (err) {
        if (!cancelled) {
          setData(null);
          setError(err instanceof Error ? err.message : "Failed to load calendar");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [month]);

  const cells = useMemo(() => {
    const days = data?.days ?? [];
    if (days.length === 0) return [];
    const leading = days[0]!.weekday;
    const blanks = Array.from({ length: leading }, () => null);
    return [...blanks, ...days];
  }, [data]);

  const dayCount = data?.days.filter((d) => d.day).length ?? 0;
  const packCount = data?.days.reduce((n, d) => n + d.packs.length, 0) ?? 0;

  return (
    <AppShell>
      <PageHeader
        eyebrow="Readings"
        title="Calendar"
        description="Browse liturgical days. Open a day to fetch, review, and edit packs."
        actions={
          <Toolbar label="Month">
            <ToolbarGroup>
              <button
                type="button"
                className="btn btn-quiet btn-sm"
                onClick={() => setMonth((m) => shiftMonth(m, -1))}
                aria-label="Previous month"
              >
                ←
              </button>
              <button type="button" className="btn btn-quiet btn-sm" disabled>
                {monthLabel(month)}
              </button>
              <button
                type="button"
                className="btn btn-quiet btn-sm"
                onClick={() => setMonth((m) => shiftMonth(m, 1))}
                aria-label="Next month"
              >
                →
              </button>
            </ToolbarGroup>
            <button
              type="button"
              className="btn btn-quiet btn-sm"
              onClick={() => setMonth(currentUtcMonth())}
            >
              Today
            </button>
            <Link className="btn btn-primary btn-sm" to="/scrape">
              Scrape
            </Link>
          </Toolbar>
        }
      />

      {data && (
        <div className="meta-strip day-meta">
          <span className="meta-pill">
            Month <strong className="mono">{month}</strong>
          </span>
          <span className="meta-pill">
            Days <strong>{dayCount}</strong>
          </span>
          <span className="meta-pill">
            Packs <strong>{packCount}</strong>
          </span>
          <span className="meta-pill">
            Env <strong>{data.environment}</strong>
          </span>
        </div>
      )}

      {error && (
        <div className="error-banner" role="alert" style={{ marginBottom: 16 }}>
          {error}
        </div>
      )}

      {data?.warnings?.map((warning) => (
        <div key={warning} className="error-banner" role="status" style={{ marginBottom: 12 }}>
          {warning}
        </div>
      ))}

      {loading && !data ? (
        <div className="panel panel-pad" role="status">
          Loading calendar…
        </div>
      ) : (
        <div className="panel calendar-grid-wrap">
          <div className="calendar-weekdays">
            {WEEKDAYS.map((label) => (
              <div key={label} className="calendar-weekday">
                {label}
              </div>
            ))}
          </div>
          <div className="calendar-grid">
            {cells.map((cell, index) =>
              cell ? (
                <Link
                  key={cell.dateKey}
                  to={`/readings/${cell.dateKey}`}
                  className={`calendar-cell tone-${statusChip(cell.packs)}`}
                >
                  <div className="calendar-cell-top">
                    <span className="calendar-daynum">{Number(cell.dateKey.slice(-2))}</span>
                    {cell.day?.color && <span className="calendar-dot" title={cell.day.color} />}
                  </div>
                  <div className="calendar-cell-title">
                    {cell.day?.liturgicalTitleEN ??
                      (cell.packs[0]?.liturgicalTitle ? cell.packs[0].liturgicalTitle : "—")}
                  </div>
                  <div className="calendar-cell-meta">
                    {cell.packs.length === 0
                      ? "Empty"
                      : cell.packs
                          .map((p) => `${p.languageCode.toUpperCase()}:${p.status[0]}`)
                          .join(" · ")}
                  </div>
                </Link>
              ) : (
                <div key={`blank-${index}`} className="calendar-cell blank" aria-hidden="true" />
              ),
            )}
          </div>
        </div>
      )}
    </AppShell>
  );
}
